import { callBetByGame, BetBySetObject, killBetBy } from "../../../assets/js/betby";
import { ExchangeWindowClose } from "../../../assets/js/common";
import { BetByGameLaunchApi } from "../api/betby.api";
import { gameApi } from "../api/game.api";
import { homeApi } from "../../home/api/home.api";
import { toastService as toast } from "../../../components/ui/toast/toast.service";
import { SHOW_MAINTENANCE_EVENT } from "../../../components/ui/modals/GameMaintenanceModal";
import { BetByRequest } from "../models/requests/betByRequest";
import { localStorageService } from "../../../services";
import errorModal from "../../../services/errorModal.service";
import { AccessMode } from "../../../models/constants/accessMode";
import { commonService } from "../../../services/common.service";
import { NavigateFunction } from "react-router-dom";

export async function launchBetBy(
  navigate: (path: string) => void,
  isTokenExpire = false,
  accessModeSource: string
): Promise<void> {
  try {
    const availability = await gameApi.checkGameAvailability({
      flag: "Vertical",
      gameId: "Sports",
    });

    if (
      availability.isSuccess &&
      availability.data &&
      !availability.data?.messagetype
    ) {
      showError(availability.data?.message);
      return;
    }

    const verticalsResponse = await homeApi.fetchActiveVerticals();
    if (!verticalsResponse.isSuccess) {
      navigate("/");
      return;
    }

    const sportsVertical = verticalsResponse.data?.details.find(
      v => v.name === "Sports"
    );
    if (!sportsVertical) {
      navigate("/");
      return;
    }

    const providersResponse = await gameApi.fetchGameProviders();
    if (!providersResponse.isSuccess) {
      navigate("/");
      return;
    }

    const betByProvider = providersResponse.data?.details.find(
      p => p.name === "BetBy"
    );
    if (!betByProvider) {
      navigate("/");
      return;
    }

    const maintenanceResponse = await gameApi.checkGameMaintenance({
      verticalId: sportsVertical.id,
      providerId: betByProvider.id,
    });

    if (!maintenanceResponse.isSuccess) {
      toast.error(
        maintenanceResponse.message || "Failed to check maintenance."
      );
      return;
    }

    if (
      maintenanceResponse.data?.message &&
      maintenanceResponse.data?.fromdate &&
      maintenanceResponse.data?.todate
    ) {
      window.dispatchEvent(
        new CustomEvent(SHOW_MAINTENANCE_EVENT, {
          detail: {
            message: maintenanceResponse.data.message,
            toDate: maintenanceResponse.data.todate,
          },
        })
      );
      return;
    }

    const token = localStorageService.getItem<string>("token");
    if (token) {
      await transferFundFromExchange();
    }

    const language = localStorageService.getItem<string>("languageCode") ?? "en";
    const fetchCommonConfigResponse = await commonService.fetchCommonConfig();
    const request: BetByRequest = {
      ipAddress: fetchCommonConfigResponse.data?.ip!,
      isLogin: !!token,
      isTokenExpire,
      language: language,
      accessMode: accessModeSource.toLowerCase() === AccessMode.Web.toLowerCase() ? 1 : 2,
    };

    const launchResponse = token
      ? await BetByGameLaunchApi.betByLaunch(request)
      : await BetByGameLaunchApi.betByLaunchWithoutToken(request);

    if (!launchResponse.isSuccess) {
      toast.error(launchResponse.message || "Failed to launch BetBy");
      return;
    }

    ExchangeWindowClose();
    const betByToken = token ? launchResponse.data?.token : null;
    callBetByGame(launchResponse.data?.brandId, launchResponse.data?.themeName, betByToken, language);
  } catch {
    showError("Something went wrong");
  }
}

export function registerBetByInterop(
  navigate: NavigateFunction,
  accessModeSource: string
): void {
  const handlers: Record<string, () => Promise<any> | void> = {
    async LaunchBetByGame() {
      if (!window.location.pathname.includes("sportsbookplus")) return;
      killBetBy();
      await launchBetBy(navigate, true, accessModeSource);
    },

    async OnLoginBetByGame() {
      navigate("/login", {
        state: { from: { pathname: window.location.pathname } },
      });
    },

    async OnRegisterBetByGame() {
      navigate("/signup", {
        state: { from: { pathname: window.location.pathname } },
      });
    },

    async OnRechargeBetByGame() {
      navigate("/deposit");
    },

    async OnTokenExpired() {
      if (!window.location.pathname.includes("sportsbookplus")) return;
      killBetBy();
      await launchBetBy(navigate, true, accessModeSource);
    },
  };

  const betByInterop = {
    invokeMethodAsync(method: string) {
      const fn = handlers[method];
      return fn ? Promise.resolve(fn()) : Promise.resolve(undefined);
    },
  };

  BetBySetObject(betByInterop);
}

async function transferFundFromExchange() {
  const balanceResponse = await homeApi.fetchUserBalance();
  if (
    balanceResponse.isSuccess &&
    (balanceResponse.data?.is9WOn ||
      (balanceResponse.data?.exchangeBalance ?? 0) > 0)
  ) {
    const transfer = await gameApi.transferFundFromExchange();
    if (!transfer.isSuccess) {
      errorModal.showErrorModal(transfer.statusCode, transfer.message, false);
    }
    else{
      await ExchangeWindowClose();
    }
  }
}

function showError(message?: string) {
  const modal = document.getElementById("common-error-modal");
  const p = modal?.querySelector("p");
  if (p) p.textContent = message || "Something went wrong";
  modal?.classList.remove("hidden");
}
